import React from "react";
import { Package, Hash, Building, Upload, Image as ImageIcon, X } from "lucide-react";
import { FormInput, FormSelect, FormTextarea, SearchableSelect } from "@/components/ui";
import { API_BASE_URL } from '@/services/api';

export const ItemForm = ({ form, setForm, errors, handleInputChange, units, categories, brands }) => (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
      <FormSelect
        icon={<Package size={14} />}
        label="Item Type"
        required
        placeholder="Select Type"
        field="type"
        options={[
          { value: "product", label: "Product" },
          { value: "service", label: "Service" },
          { value: "material", label: "Material" },
        ]}
        value={form.type}
        onChange={handleInputChange}
        error={errors.type}
      />
      <FormSelect
        icon={<Package size={14} />}
        label="Units"
        required
        placeholder="Select Unit"
        field="units"
        options={units}
        value={form.units}
        onChange={handleInputChange}
        error={errors.units}
      />
      <FormSelect
        icon={<Package size={14} />}
        label="Category"
        required
        placeholder="Select Category"
        field="category"
        options={categories}
        value={form.category}
        onChange={handleInputChange}
        error={errors.category}
      />
      <FormSelect
        icon={<Building size={14} />}
        label="Brand"
        required
        placeholder="Select Brand"
        field="brand"
        options={brands}
        value={form.brand}
        onChange={handleInputChange}
        error={errors.brand}
      />
      <FormInput
        icon={<Package size={14} />}
        label="Item Name"
        required
        placeholder="Item Name"
        field="itemName"
        value={form.itemName}
        onChange={handleInputChange}
        error={errors.itemName}
      />
      <FormInput
        icon={<Hash size={14} />}
        label="Item Code"
        placeholder="Item Code"
        field="itemCode"
        value={form.itemCode}
        onChange={handleInputChange}
        error={errors.itemCode}
        required
      />
      <FormInput
        icon={<Hash size={14} />}
        label="MOQ"
        required
        placeholder="MOQ"
        field="moq"
        value={form.moq}
        onChange={handleInputChange}
        error={errors.moq}
        type="number"
      />
      <FormSelect
        icon={<Package size={14} />}
        label="Fixing Type"
        required
        placeholder="Select Fixing Type"
        field="fixingType"
        options={[
          { value: "Ready to Fix", label: "Ready to Fix" },
          { value: "Loose", label: "Loose" },
          { value: "Custom", label: "Custom" },
        ]}
        value={form.fixingType}
        onChange={handleInputChange}
        error={errors.fixingType}
      />
    </div>

    <div className="border-t border-slate-100 my-6 pt-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <SearchableSelect
          icon={<Package size={14} />}
          label="Quality"
          required
          placeholder="Quality"
          options={[
            { value: "Premium", label: "Premium" },
            { value: "Semi Premium", label: "Semi Premium" },
            { value: "Special MED Models (Only Premium)", label: "Special MED Models (Only Premium)" }
          ]}
          value={form.quality}
          onChange={(val) => handleInputChange({ target: { name: 'quality', value: val } })}
          allowCustom={true}
          error={errors.quality}
        />
        <FormInput
          icon={<Package size={14} />}
          label="Colour"
          placeholder="Colour"
          field="colour"
          value={form.colour}
          onChange={handleInputChange}
        />
        <FormInput
          icon={<Package size={14} />}
          label="Profile"
          placeholder="Profile"
          field="profile"
          value={form.profile}
          onChange={handleInputChange}
        />
        <FormInput
          icon={<Package size={14} />}
          label="Height"
          placeholder="Height"
          field="height"
          value={form.height}
          onChange={handleInputChange}
        />
        <FormInput
          icon={<Package size={14} />}
          label="Width"
          placeholder="Width"
          field="width"
          value={form.width}
          onChange={handleInputChange}
        />
        <FormInput
          icon={<Package size={14} />}
          label="Thickness"
          placeholder="Thickness"
          field="thickness"
          value={form.thickness}
          onChange={handleInputChange}
        />
        <FormInput
          icon={<Hash size={14} />}
          label="HSN/SAC Code"
          placeholder="HSN/SAC Code"
          field="hsnSacCode"
          value={form.hsnSacCode}
          onChange={handleInputChange}
          error={errors.hsnSacCode}
        />
        <FormInput
          icon={<Hash size={14} />}
          label="Barcode"
          placeholder="Barcode"
          field="barcode"
          value={form.barcode}
          onChange={handleInputChange}
          error={errors.barcode}
        />
      </div>
    </div>

    <div className="border-t border-slate-100 my-6 pt-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isUnique"
              name="isUnique"
              className="w-4 h-4 text-brand-blue border-slate-300 rounded focus:ring-brand-blue transition-all cursor-pointer"
              checked={form.isUnique || false}
              onChange={handleInputChange}
            />
            <label
              htmlFor="isUnique"
              className="text-xs font-bold text-slate-500 uppercase tracking-widest cursor-pointer"
            >
              Is Unique Item
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isBatchExpiry"
              name="isBatchExpiry"
              className="w-4 h-4 text-brand-blue border-slate-300 rounded focus:ring-brand-blue transition-all cursor-pointer"
              checked={form.isBatchExpiry || false}
              onChange={handleInputChange}
            />
            <label
              htmlFor="isBatchExpiry"
              className="text-xs font-bold text-slate-500 uppercase tracking-widest cursor-pointer"
            >
              Has Batch/Expiry
            </label>
          </div>
        </div>
        {form.isUnique && (
          <FormInput
            icon={<Hash size={14} />}
            label="Unique Name"
            required
            placeholder="Unique identifier"
            field="uniqueName"
            value={form.uniqueName}
            onChange={handleInputChange}
            error={errors.uniqueName}
          />
        )}
      </div>
    </div>

    {/* Image Upload Field */}
    <div className="border-t border-slate-100 my-6 pt-6">
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
          <ImageIcon size={14} className="text-slate-400" /> Image
        </label>
        <div
          className="relative w-full max-w-[200px] border-2 border-dashed border-slate-200 rounded-lg p-1.5 hover:border-[#C91D25]/50 transition-colors bg-slate-50/50 outline-none min-h-[80px] flex items-center justify-center group"
          tabIndex={0}
          onPaste={async (e) => {
            const clipItems = e.clipboardData?.items;
            if (!clipItems) return;
            for (let i = 0; i < clipItems.length; i++) {
              if (clipItems[i].type.indexOf('image') !== -1) {
                const file = clipItems[i].getAsFile();
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => setForm({ ...form, image: reader.result });
                  reader.readAsDataURL(file);
                }
                break;
              }
            }
          }}
        >
          {!form.image ? (
            <div className="flex flex-col items-center justify-center gap-1.5 py-2">
              <span className="text-[10px] text-slate-400">Ctrl+V to paste</span>
              <div className="flex gap-2">
                <label className="flex items-center justify-center p-1.5 bg-slate-200 text-slate-700 rounded shadow-sm cursor-pointer hover:bg-slate-300 transition-colors" title="Upload Image">
                  <Upload size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => setForm({ ...form, image: reader.result });
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
                <button
                  type="button"
                  title="Paste Image from Clipboard"
                  className="flex items-center justify-center p-1.5 bg-slate-200 text-slate-700 rounded shadow-sm cursor-pointer hover:bg-slate-300 transition-colors"
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    try {
                      const clipboardItems = await navigator.clipboard.read();
                      for (const clipboardItem of clipboardItems) {
                        if (clipboardItem.types.some(t => t.startsWith('image/'))) {
                          const type = clipboardItem.types.find(t => t.startsWith('image/'));
                          const blob = await clipboardItem.getType(type);
                          const reader = new FileReader();
                          reader.onloadend = () => setForm({ ...form, image: reader.result });
                          reader.readAsDataURL(blob);
                          return;
                        }
                      }
                    } catch (err) {}
                  }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="relative w-full h-full min-h-[80px] flex items-center justify-center p-1 bg-white rounded">
              <img src={form.image.startsWith('data:') || form.image.startsWith('http') ? form.image : `${API_BASE_URL}/api/inventory/settings/images/${form.image}`} alt="Item" className="max-h-[80px] max-w-[150px] object-contain rounded" />
              <button
                type="button"
                className="absolute top-1 right-1 p-0.5 bg-red-100 text-red-600 hover:bg-red-200 rounded-full transition-colors"
                onClick={() => setForm({ ...form, image: null })}
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Status field before description */}
    <div className="border-t border-slate-100 my-6 pt-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <FormSelect
          icon={<Package size={14} />}
          label="Status"
          field="active"
          options={[
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ]}
          value={form.active ? "active" : "inactive"}
          onChange={(e) =>
            setForm({
              ...form,
              active: e.target.value === "active",
            })
          }
        />
      </div>
    </div>

    <FormTextarea
      icon={<Package size={14} />}
      label="Description"
      placeholder="Item description..."
      field="itemDescription"
      value={form.itemDescription}
      onChange={handleInputChange}
      error={errors.itemDescription}
    />
  </div>
);
